export const okAction = (): ValidResult<boolean, boolean> =>
  ResultFactory.ok<boolean, boolean>(true);
export const ok = <TResult, TError>(value: TResult): ValidResult<TResult, TError> =>
  ResultFactory.ok<TResult, TError>(value);
export const err = <TError>(errors: Array<TError> = []): InvalidResult<TError> =>
  ResultFactory.err<TError>(errors);

export class ResultFactory {
  public static ok<SResult, SError>(value: SResult): ValidResult<SResult, SError> {
    return new ValidResult<SResult, SError>(value);
  }

  public static err<SError>(errors: SError[] = []): InvalidResult<SError> {
    return new InvalidResult<SError>(errors);
  }
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace Result {
  export function fromBoolean<TError>(
    isValid: boolean,
    errors?: TError[]
  ): Result<boolean, TError> {
    return isValid ? ok(true) : err(errors);
  }
}

export type Result<TResult, TError> = ValidResult<TResult, TError> | InvalidResult<TError>;

export interface IResult<TResult, TError> {
  isValid: boolean;
  value: TResult;
  errors: TError[];
}

export interface IAnyResult {
  isValid: boolean;
  value: any;
  errors: any;
}

export abstract class ResultBase<TResult, TError> implements IResult<TResult, TError>, IAnyResult {
  public readonly isValid: boolean;
  private readonly val: TResult;
  private readonly errs: TError[];

  protected constructor(isValid: boolean, errors: TError[] = [], value: TResult) {
    if (isValid && errors && errors.length > 0) {
      throw new Error('InvalidOperationException: valid results cannot contain errors');
    }

    this.isValid = isValid;
    this.val = value;
    this.errs = errors;
  }

  public get value(): TResult {
    return this.val;
  }

  public get errors(): TError[] {
    return this.errs;
  }

  get hasFailed(): boolean {
    return !this.isValid;
  }
}

// noinspection BadExpressionStatementJS
export class ValidResult<TResult, TError> extends ResultBase<TResult, TError> {
  public constructor(value: TResult) {
    super(true, [], value);
  }

  ifSuccess(callback: (resultValue: TResult) => void): ValidResult<TResult, TError> {
    callback(this.value);
    return this;
  }

  async ifSuccessAsync(
    callbackAsync: (resultValue: TResult) => Promise<void>
  ): Promise<ValidResult<TResult, TError>> {
    await callbackAsync(this.value);
    return this;
  }

  ifError(callback: (resultValue: Array<TError>) => void): ValidResult<TResult, TError> {
    callback;
    return this;
  }

  async ifErrorAsync(
    callback: (resultValue: Array<TError>) => Promise<void>
  ): Promise<ValidResult<TResult, TError>> {
    callback;
    return this;
  }

  map<TTarget>(callback: (resultValue: TResult) => TTarget): ValidResult<TTarget, TError> {
    const target = callback(this.value);
    return ok(target);
  }

  async mapAsync<TTarget>(
    callback: (resultValue: TResult) => Promise<TTarget>
  ): Promise<ValidResult<TTarget, TError>> {
    const target = await callback(this.value);
    return ok(target);
  }
}

// noinspection BadExpressionStatementJS
export class InvalidResult<TError> extends ResultBase<false, TError> {
  public constructor(errors: TError[]) {
    super(false, errors, false);
  }

  ifError(callback: (resultValue: Array<TError>) => void): InvalidResult<TError> {
    callback(this.errors);
    return this;
  }

  async ifErrorAsync(
    callback: (resultValue: Array<TError>) => Promise<void>
  ): Promise<InvalidResult<TError>> {
    await callback(this.errors);
    return this;
  }

  ifSuccess(callback: (resultValue: any) => void): InvalidResult<TError> {
    callback;
    return this;
  }

  async ifSuccessAsync(
    callbackAsync: (resultValue: any) => Promise<void>
  ): Promise<InvalidResult<TError>> {
    // noinspection BadExpressionStatementJS
    callbackAsync;
    return this;
  }

  map<TTarget>(callback: (resultValue: any) => TTarget): InvalidResult<TError> {
    // noinspection BadExpressionStatementJS
    callback;
    return this;
  }

  async mapAsync<TTarget>(
    callback: (resultValue: any) => Promise<TTarget>
  ): Promise<InvalidResult<TError>> {
    // noinspection BadExpressionStatementJS
    callback;
    return this;
  }
}
